/**
 * Authentication service
 */
import { apiClient } from './api';
import type { AuthResponse, LoginRequest, RegisterRequest, OAuthUrlResponse, User } from '../types/auth';

export const authService = {
  /**
   * Login with email and password
   */
  async login(data: LoginRequest): Promise<AuthResponse> {
    const formData = new FormData();
    formData.append('username', data.email);
    formData.append('password', data.password);

    const response = await apiClient.post<AuthResponse>('/auth/login', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Register new user
   */
  async register(data: RegisterRequest): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/register', {
      email: data.email,
      password: data.password,
    });
    return response.data;
  },

  /**
   * Logout
   */
  async logout(): Promise<void> {
    await apiClient.post('/auth/logout');
  },

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<{ access_token: string }> {
    const response = await apiClient.post('/auth/refresh', {
      refresh_token: refreshToken,
    });
    return response.data;
  },

  /**
   * Get current user
   */
  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<User>('/auth/me');
    return response.data;
  },

  /**
   * Get Google OAuth URL
   */
  async getGoogleAuthUrl(): Promise<OAuthUrlResponse> {
    const response = await apiClient.get<OAuthUrlResponse>('/oauth/google/url', {
      params: {
        redirect_uri: `${window.location.origin}/auth/google/callback`,
      },
    });
    return response.data;
  },

  /**
   * Get Microsoft OAuth URL
   */
  async getMicrosoftAuthUrl(): Promise<OAuthUrlResponse> {
    const response = await apiClient.get<OAuthUrlResponse>('/oauth/microsoft/url', {
      params: {
        redirect_uri: `${window.location.origin}/auth/microsoft/callback`,
      },
    });
    return response.data;
  },

  /**
   * Handle Google OAuth callback
   */
  async handleGoogleCallback(code: string, state: string): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/oauth/google/callback', null, {
      params: { code, state },
    });
    return response.data;
  },

  /**
   * Handle Microsoft OAuth callback
   */
  async handleMicrosoftCallback(code: string, state: string): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/oauth/microsoft/callback', null, {
      params: { code, state },
    });
    return response.data;
  },

  /**
   * Change password
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await apiClient.post('/auth/change-password', {
      old_password: oldPassword,
      new_password: newPassword,
    });
  },
};
